/**
 * lifeDataService — 個人生活中心資料同步
 *
 * 所有資料以後端 user_life_data 為主（非 localStorage）。
 * localStorage 只做 session 快取，登入時從 API 拉取覆蓋，登出時清除。
 *
 * API: GET / PATCH  /api/users/me/life-data
 */

import axios from '../api/axios';

const BASE = '/api/users/me/life-data';

// ── 資料結構 ──────────────────────────────────────────────────────────────────

export interface SavedCity {
  name:   string;
  nameEn: string;
  lat:    number;
  lng:    number;
}


export interface HealthLocation {
  lat:       number;
  lng:       number;
  placeName: string;
  country:   string;
  timezone:  string;
}

export interface HealthValueSchema {
  label: string;
  unit:  string;
}

export interface HealthTypeConfig {
  name:        string;
  valueSchema: HealthValueSchema[];
  conditions:  string[];
}

export interface HealthRecord {
  id:        string;
  ts:        string;          // 當地時間 "2026-05-19T05:54"
  timezone:  string;          // "Asia/Taipei"
  type:      string;
  condition: string;
  values:    number[];
  food:      string;
  medicine:  string;
  notes:     string;
  location?: HealthLocation;
}

export interface HealthData {
  config:  { types: HealthTypeConfig[] };
  records: HealthRecord[];
}

export interface LifeData {
  my_cities:  SavedCity[];
  trips:      any[];          // tripService 的 Trip[]（原始格式）
  trip_plans: any[];          // tripPlanService 的 TripPlan[]
  health:     HealthData;
}

// ── 預設健康設定（首次使用時自動注入）────────────────────────────────────────

const DEFAULT_HEALTH_CONFIG: HealthTypeConfig[] = [
  {
    name: '血糖',
    valueSchema: [{ label: '血糖值', unit: 'mg/dL' }],
    conditions: ['空腹(早餐前)', '早餐後', '中餐前', '中餐後', '晚餐前', '晚餐後'],
  },
  {
    name: '血壓',
    valueSchema: [
      { label: '收縮壓', unit: 'mmHg' },
      { label: '舒張壓', unit: 'mmHg' },
      { label: '心跳',   unit: 'bpm'  },
    ],
    conditions: ['起床', '早餐前', '午餐前', '睡前', '運動後'],
  },
  {
    name: '飲食',
    valueSchema: [],
    conditions: ['早餐', '午餐', '晚餐', '點心'],
  },
];

// ── 血糖 condition 升版 migration ────────────────────────────────────────────
// 舊 5 個歧義選項 → 新 6 個明確時段；偵測到舊版自動升級並存回後端。

const OLD_GLUCOSE_CONDITIONS = new Set(['空腹', '飯前', '飯後', '起床', '睡前']);
const NEW_GLUCOSE_CONDITIONS = ['空腹(早餐前)', '早餐後', '中餐前', '中餐後', '晚餐前', '晚餐後'];

function migrateGlucoseConditions(health: HealthData): { health: HealthData; migrated: boolean } {
  const types = health.config.types.map(t => {
    if (t.name !== '血糖') return t;
    const isOld = t.conditions.length > 0 && t.conditions.every(c => OLD_GLUCOSE_CONDITIONS.has(c));
    return isOld ? { ...t, conditions: NEW_GLUCOSE_CONDITIONS } : t;
  });
  const migrated = types.some((t, i) => t !== health.config.types[i]);
  return { health: migrated ? { ...health, config: { types } } : health, migrated };
}

const EMPTY_LIFE_DATA: LifeData = {
  my_cities:  [],
  trips:      [],
  trip_plans: [],
  health:     { config: { types: DEFAULT_HEALTH_CONFIG }, records: [] },
};

// ── Session 快取 key ──────────────────────────────────────────────────────────

const CACHE_KEY = '_ld_cache';   // sessionStorage key（分頁級別，登出自動清）

function readCache(): LifeData | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function writeCache(data: LifeData): void {
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(data)); }
  catch { /* storage full — ignore */ }
}

function clearCache(): void {
  sessionStorage.removeItem(CACHE_KEY);
  // 也清舊的 localStorage keys（若還有殘留）
  ['lc_my_cities', 'lc_trips', 'lc_trip_plans'].forEach(k => localStorage.removeItem(k));
}

// ── API helpers ───────────────────────────────────────────────────────────────

async function apiFetch<T>(path: string, method: 'GET' | 'PATCH' = 'GET', body?: unknown): Promise<T> {
  const res = method === 'PATCH'
    ? await axios.patch<T>(path, body)
    : await axios.get<T>(path);
  return res.data;
}

// ── 公開 API ──────────────────────────────────────────────────────────────────

/**
 * 載入個人生活資料（登入後呼叫一次）
 * 後端資料 > 快取，保證拿到最新。
 */
export async function loadLifeData(): Promise<LifeData> {
  const remote = await apiFetch<Partial<LifeData>>(BASE);

  // 確保 health 有完整結構（首次使用者後端返回 {} 空物件）
  const health = remote.health ?? { config: { types: [] }, records: [] };
  if (!health.config?.types?.length) {
    health.config = { types: DEFAULT_HEALTH_CONFIG };
  }
  if (!Array.isArray(health.records)) {
    health.records = [];
  }

  // 偵測舊 conditions → 自動升版並存回後端（一次性 migration）
  const { health: migratedHealth, migrated } = migrateGlucoseConditions(health);
  if (migrated) {
    saveHealth(migratedHealth).catch(() => {}); // silent ok: best-effort config migration
  }

  const data: LifeData = {
    my_cities:  remote.my_cities  ?? [],
    trips:      remote.trips      ?? [],
    trip_plans: remote.trip_plans ?? [],
    health: migratedHealth,
  };

  writeCache(data);
  return data;
}

/**
 * 取得快取資料（同步，不發 API）。
 * 用於元件掛載時快速讀取，避免每次渲染都打 API。
 */
export function getCachedLifeData(): LifeData {
  const cached = readCache();
  if (!cached) return { ...EMPTY_LIFE_DATA };

  // stale / 舊版 / corrupt cache shape guard（porsche #14 空白頁 root fix）：
  // 每欄位顯式 fallback。不能用 {...EMPTY, ...cached}——spread 對 undefined 值仍覆蓋成 undefined，
  // 後續 migrateGlucoseConditions 讀 health.config.types 會 TypeError → useState initializer throw → 整棵 route 卸載空白。
  const rawHealth = (cached.health && typeof cached.health === 'object') ? cached.health : undefined;
  const safeHealth: HealthData = {
    config: Array.isArray(rawHealth?.config?.types)
      ? rawHealth!.config
      : { types: DEFAULT_HEALTH_CONFIG },
    records: Array.isArray(rawHealth?.records) ? rawHealth!.records : [],
  };
  const safe: LifeData = {
    my_cities:  Array.isArray(cached.my_cities)  ? cached.my_cities  : [],
    trips:      Array.isArray(cached.trips)      ? cached.trips      : [],
    trip_plans: Array.isArray(cached.trip_plans) ? cached.trip_plans : [],
    health:     safeHealth,
  };

  const { health, migrated } = migrateGlucoseConditions(safe.health);
  if (migrated) {
    const next = { ...safe, health };
    writeCache(next);
    return next;
  }
  return { ...safe, health };
}

/**
 * 部分更新：只傳要改的欄位，後端 PATCH 不影響其他欄位。
 * 同時更新 session 快取。
 */
export async function patchLifeData(patch: Partial<LifeData>): Promise<LifeData> {
  const updated = await apiFetch<LifeData>(BASE, 'PATCH', patch);
  writeCache(updated);
  return updated;
}

/**
 * 登出時清除所有本地快取。
 */
export function clearLifeData(): void {
  clearCache();
}

// ── 欄位級快捷方法 ────────────────────────────────────────────────────────────

export async function saveMyCities(cities: SavedCity[]): Promise<void> {
  const cached = getCachedLifeData();
  const updated = await patchLifeData({ my_cities: cities });
  // 同步回快取（patchLifeData 已處理）
  void updated;
}

/**
 * 旅程照片元素。Task-32 後：upload / backfill 後是 {url, file_object_id} dict；
 * 舊資料仍可能是純 url 字串。兩種都要能渲染（hotfix）。
 */
export type TripPhoto = string | { url?: string; file_object_id?: number };

export async function uploadTripPhoto(file: File): Promise<TripPhoto> {
  const form = new FormData();
  form.append('file', file);
  const res = await axios.post<{ url: string; file_object_id?: number | null }>(`${BASE}/photos`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const { url, file_object_id } = res.data;
  // 有 file_object_id → 存 dict（對齊 G3.1 backfill 結構）；否則存 url 字串（相容）
  return file_object_id != null ? { url, file_object_id } : url;
}

/** Prefix the backend base URL so <img src> resolves correctly in both dev and prod. */
export function buildPhotoUrl(path: string): string {
  if (!path || path.startsWith('http')) return path;
  const base = (axios.defaults.baseURL ?? '').replace(/\/$/, '');
  return `${base}${path}`;
}

/** 取 photo 的穩定 key（去重 / 刪除比對用，避免 dict 當 React key 變 [object Object]）。 */
export function tripPhotoKey(p: TripPhoto): string {
  if (typeof p === 'string') return p;
  if (p.file_object_id != null) return `fobj:${p.file_object_id}`;
  return p.url ?? '';
}

/**
 * 解析 photo 元素 → 渲染來源。
 * - 有 file_object_id → 走認證 API（需 AuthImage，瀏覽器 <img> 不帶 token）
 * - 否則（字串 / 只有 url 的 dict）→ 走 buildPhotoUrl（StaticFiles，G3.2 拔前仍可用）
 */
export function resolveTripPhoto(p: TripPhoto): { authSrc?: string; plainSrc?: string } {
  if (typeof p === 'string') return { plainSrc: buildPhotoUrl(p) };
  if (p.file_object_id != null) return { authSrc: `${BASE}/photos/${p.file_object_id}` };
  if (p.url) return { plainSrc: buildPhotoUrl(p.url) };
  return {};
}

export async function saveTrips(trips: any[]): Promise<void> {
  await patchLifeData({ trips });
}

export async function saveTripPlans(tripPlans: any[]): Promise<void> {
  await patchLifeData({ trip_plans: tripPlans });
}

export async function saveHealth(health: HealthData): Promise<void> {
  await patchLifeData({ health });
}

// ── 健康紀錄 CRUD ─────────────────────────────────────────────────────────────

export function makeHealthId(): string {
  return `hr_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
}

export async function addHealthRecord(record: Omit<HealthRecord, 'id'>): Promise<HealthRecord> {
  const cached = getCachedLifeData();
  const newRecord: HealthRecord = { ...record, id: makeHealthId() };
  const health: HealthData = {
    config:  cached.health.config,
    records: [...(cached.health.records ?? []), newRecord],
  };
  await saveHealth(health);
  return newRecord;
}

export async function updateHealthRecord(id: string, patch: Partial<HealthRecord>): Promise<void> {
  const cached = getCachedLifeData();
  const health: HealthData = {
    ...cached.health,
    records: cached.health.records.map(r => r.id === id ? { ...r, ...patch } : r),
  };
  await saveHealth(health);
}

export async function deleteHealthRecord(id: string): Promise<void> {
  const cached = getCachedLifeData();
  const health: HealthData = {
    ...cached.health,
    records: cached.health.records.filter(r => r.id !== id),
  };
  await saveHealth(health);
}

/** 新增自訂類型（若同名已存在則忽略） */
export async function addHealthType(typeConfig: HealthTypeConfig): Promise<void> {
  const cached = getCachedLifeData();
  const existing = cached.health.config.types;
  if (existing.some(t => t.name === typeConfig.name)) return;
  const health: HealthData = {
    ...cached.health,
    config: { types: [...existing, typeConfig] },
  };
  await saveHealth(health);
}

/** 新增條件至指定類型（若已存在則忽略） */
export async function addHealthCondition(typeName: string, condition: string): Promise<void> {
  const cached = getCachedLifeData();
  const health: HealthData = {
    ...cached.health,
    config: {
      types: cached.health.config.types.map(t =>
        t.name === typeName && !t.conditions.includes(condition)
          ? { ...t, conditions: [...t.conditions, condition] }
          : t,
      ),
    },
  };
  await saveHealth(health);
}
